import {testIdProps} from '@github-ui/test-id-props'
import {KebabHorizontalIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, useConfirm, useMergedRefs} from '@primer/react'
import {useCallback, useMemo, useRef} from 'react'

import type {ClientMemexWorkflow, MemexWorkflowPersisted} from '../../api/workflows/contracts'
import {isAutoAddWorkflow, isWorkflowPersisted} from '../../helpers/workflow-utilities'
import {useWorkflows} from '../../state-providers/workflows/use-workflows'
import {WorkflowResources} from '../../strings'
import {DuplicateWorkflowButton} from './duplicate-workflow-button'
import styles from './workflow-menu.module.css'

type WorkflowMenuProps = {
  workflow: ClientMemexWorkflow
  showDelete: boolean
  isOpen: boolean
  ref: React.Ref<HTMLButtonElement>
}

export const WorkflowMenu = ({ref, workflow, showDelete, isOpen}: WorkflowMenuProps) => {
  const {deleteWorkflow, setWorkflowMenuState, handleReturnFocus} = useWorkflows()
  const confirm = useConfirm()

  const onButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setWorkflowMenuState(workflow.clientId)
      if (isOpen) handleReturnFocus(workflow.clientId)
    },
    [handleReturnFocus, isOpen, setWorkflowMenuState, workflow.clientId],
  )

  const onDelete = useCallback(
    async (persistedWorkflow: MemexWorkflowPersisted) => {
      const shouldDelete = await confirm({
        title: WorkflowResources.deleteWorkflowConfirmationTitle,
        content: WorkflowResources.deleteWorkflowConfirmationContent,
        confirmButtonContent: WorkflowResources.deleteWorkflowConfirmationButton,
        confirmButtonType: 'danger',
      })

      setWorkflowMenuState(workflow.clientId)

      if (shouldDelete) {
        void deleteWorkflow(persistedWorkflow.number)
      } else {
        handleReturnFocus(workflow.clientId)
      }
    },
    [confirm, deleteWorkflow, handleReturnFocus, setWorkflowMenuState, workflow.clientId],
  )

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (open !== isOpen) setWorkflowMenuState(workflow.clientId)
      if (!open) handleReturnFocus(workflow.clientId)
    },
    [handleReturnFocus, isOpen, setWorkflowMenuState, workflow.clientId],
  )

  const anchorRef = useRef<HTMLButtonElement>(null)
  const mergedAnchorRef = useMergedRefs(ref, anchorRef)

  const items = useMemo<Array<React.ReactElement>>(() => {
    const menuItems = []
    if (isWorkflowPersisted(workflow)) {
      if (showDelete) {
        menuItems.push(
          <ActionList.Item
            key={`${workflow.clientId}-delete-option`}
            variant="danger"
            onSelect={() => onDelete(workflow)}
            {...testIdProps(`workflow-nav-item-menu-delete-${workflow.clientId}`)}
          >
            <ActionList.LeadingVisual>
              <TrashIcon />
            </ActionList.LeadingVisual>
            {WorkflowResources.deleteWorkflowCallToAction}
          </ActionList.Item>,
        )
      }

      if (isAutoAddWorkflow(workflow))
        menuItems.push(
          <DuplicateWorkflowButton key={`${workflow.clientId}-duplicate-option`} workflowToDuplicate={workflow} />,
        )
    }
    return menuItems
  }, [onDelete, showDelete, workflow])

  return (
    <>
      {items.length > 0 ? (
        <>
          <IconButton
            id={`workflow-nav-item-menu-${workflow.clientId}`}
            key={`workflow-nav-item-menu-${workflow.clientId}`}
            ref={mergedAnchorRef}
            icon={KebabHorizontalIcon}
            variant="invisible"
            size="small"
            aria-label="Open workflow options"
            aria-haspopup="true"
            aria-expanded={isOpen}
            onClick={onButtonClick}
            className={styles.menuIconButton}
            {...testIdProps(`workflow-nav-item-menu-${workflow.clientId}`)}
          />
          <ActionMenu anchorRef={anchorRef} open={isOpen} onOpenChange={onOpenChange}>
            <ActionMenu.Overlay className={styles.actionMenuOverlay}>
              <ActionList
                key={`workflow-nav-item-menu-list-${workflow.clientId}`}
                {...testIdProps(`workflow-nav-item-menu-list-${workflow.clientId}`)}
              >
                {items}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </>
      ) : null}
    </>
  )
}

WorkflowMenu.displayName = 'WorkflowMenu'
